import {
  action,
  observable,
  runInAction,
} from 'mobx';
import FilesService from '../services/files.service';
import AmazonService from '../services/amazon.service';
import commonStore from '../../common/stores/common.store';
import filesStore from './files.store';
import { getFileExtension, removeFileExtension } from '../../common/helpers/common.helpers';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  EMPTY_STRING,
  FILE_REMOVE_SUCCESS,
  FILE_DOWNLOAD_SUCCESS
} from '../../constants/constants';

export class FileDetailsStore {

  @observable downloadURL = EMPTY_STRING;

  @observable file = {};

  @observable fileExtension = EMPTY_STRING;

  @observable name = EMPTY_STRING;

  @observable suggestedName = EMPTY_STRING;

  @observable tags = [];

  @observable transcription = EMPTY_STRING;

  /**
   * Sets transcription
   * @param {string} transcription - The transcription to set.
   * */
  @action setTranscription = (transcription) => {
    this.transcription = transcription;
  };


  /**
   * Sets name and saves to DB if validation doesn't fail
   * @param {string} name - The file name to set.
   * */
  @action setName = async (name) => {
    try {
      const validationResponse = await FilesService.validateName(name, this.id, this.fileExtension);
      runInAction(() => {
        this.name = name;
        //this.name = name+'.'+this.fileExtension;
        if (validationResponse && validationResponse.suggested_filename !== name) {
          this.suggestedName = validationResponse.suggested_filename;
        } else {
          this.suggestedName = EMPTY_STRING;
          this.saveFileDetails();
        }
      });

    } catch (e) {
      runInAction(() => {
        commonStore.show(e.message, ALERT_ERROR);
      });
    }
  };

  /**
   * Reset suggested file name and error state
   * */
  @action clearSuggestedName = () => {
    this.suggestedName = EMPTY_STRING;
  };

  /**
   * Add tag to tags and save the form to api
   * @param {object} tag - The tag to add.
   * */
  @action addTag = async (tag) => {
    if (this.tags.includes(tag)) return;
    this.tags.push(tag);
    await this.saveFileDetails();
  };

  /**
   * Remove tag from tags and save the form to api
   * @param {object} inputTag - The tag to remove.
   * */
  @action removeTag = async (inputTag) => {
    this.tags = this.tags.filter(tag => tag !== inputTag);
    await this.saveFileDetails();
  };

  /**
   * Loads file based on input id .
   * @param {number} id - The file id.
   * */
  @action loadFile = async (id) => {
    try {
      commonStore.showSpinner();
      const file = await FilesService.loadById(id);
      runInAction(() => {
        this.file = file;
        this.id = file.id;
        this.name = removeFileExtension(file.name);
        this.downloadURL = file.download_url;
        this.fileExtension = getFileExtension(file.name);
        this.tags = file.tags === null ? [] : file.tags;
        this.transcription = file.transcription === null ? EMPTY_STRING : file.transcription;
        commonStore.hideSpinner();
        commonStore.updateProgress();
      });
    } catch (e) {
      commonStore.hideSpinner();
      runInAction(() => { this.file = {}; });
    }
  };

  /**
   * Get Download audio URL
   * @param {number} id audio ID
   */
  @action downloadUrl = async (id) => {
    commonStore.showSpinner();
    try {
      const downloadObj = await FilesService.downloadById(id);
      commonStore.hideSpinner();
      runInAction(() => { this.downloadURL = downloadObj.url; });
    } catch (e) {
      runInAction(() => {
        commonStore.show(e.message, ALERT_ERROR);
        commonStore.hideSpinner();
      });
    }
  };

  /**
   * Saves file details.
   * */
  @action saveFileDetails = async () => {
    try {
      const formData = {
        name: `${this.name}.${this.fileExtension}`,
        tags: this.tags,
        transcription: this.transcription
      };
      const response = await FilesService.saveFileDetails(formData, this.file.id);
      runInAction(() => { this.file = response; });
    } catch (e) {
      runInAction(() => { this.file = {}; });
    }
  };

  /**
   * Removes a file.
   * */
  @action removeFile = async () => {
    commonStore.showSpinner();
    try {
      await FilesService.remove(this.file.id);
      filesStore.removeFile(this.file.id);
      commonStore.show(FILE_REMOVE_SUCCESS, ALERT_SUCCESS);

      commonStore.hideSpinner();
    } catch (e) {
      commonStore.hideSpinner();
    }
  };

  @action clear = () => {
    this.file = {};
    this.transcription = EMPTY_STRING;
    this.tags = [];
    this.name = EMPTY_STRING;
    this.suggestedName = EMPTY_STRING;
  };

  /**
   * Triggers message zip and download to browser
   */
  @action downloadFileFromAmazon = async () => {
    commonStore.showSpinner();
    try {

      const files = [{
        created: this.file.created,
        duration: this.file.duration,
        id: this.file.id,
        name: `${this.name}.${this.fileExtension}`,
        extension: this.fileExtension,
        url: this.downloadURL,
        tags: this.tags,
        transcription: this.transcription,
      }];
      await AmazonService.downloadZip(files);
      commonStore.hideSpinner();
      commonStore.show(FILE_DOWNLOAD_SUCCESS, ALERT_SUCCESS);

    } catch (e) {
      commonStore.show(e.message, ALERT_ERROR);
      commonStore.hideSpinner();
    }
  };

}

export default new FileDetailsStore();



// WEBPACK FOOTER //
// ./src/messages/stores/fileDetails.store.js